import { uuid } from './uuid';
// ─── estimateUtils.js ─────────────────────────────────────────────────────────
// Pure helpers for the lineItems[] data model.
// No React, no imports — safe to use anywhere.

/**
 * Generate a new lineItem shell with safe defaults.
 * @param {Partial<LineItem>} overrides
 */
export function makeLineItem(overrides = {}) {
  return {
    id: uuid(),
    description: '',
    qty: 1,
    unitPrice: 0,
    cost: 0,       // shop's COGS per unit (used for margin calc)
    total: 0,
    category: 'material', // 'material' | 'labor' | 'addon' | 'discount'
    ...overrides,
  };
}

/**
 * Recalculate an item's total from qty × unitPrice.
 * Mutates nothing — returns a new item.
 */
export function recalcItem(item) {
  const total = parseFloat(
    ((Number(item.qty) || 0) * (Number(item.unitPrice) || 0)).toFixed(2)
  );
  return { ...item, total };
}

/**
 * Derive aggregate totals from an estimate's lineItems[].
 * Returns: { subtotal, cogs, margin, marginPct }
 *
 * subtotal  — sum of all item totals (includes discounts, which are negative)
 * cogs      — sum of (qty × cost) for each item
 * margin    — subtotal − cogs
 * marginPct — margin / subtotal (0–1), or null if subtotal === 0
 */
export function calcEstimateTotals(estimate) {
  const items = Array.isArray(estimate?.lineItems) ? estimate.lineItems : [];

  let subtotal = 0;
  let cogs = 0;

  for (const li of items) {
    const total = Number(li.total) || 0;
    const itemCogs = (Number(li.qty) || 0) * (Number(li.cost) || 0);
    subtotal += total;
    cogs += itemCogs;
  }

  subtotal = parseFloat(subtotal.toFixed(2));
  cogs = parseFloat(cogs.toFixed(2));
  const margin = parseFloat((subtotal - cogs).toFixed(2));
  const marginPct = subtotal > 0 ? margin / subtotal : null;

  return { subtotal, cogs, margin, marginPct };
}

/**
 * Derive the scalar `total` field kept on estimates for backward compat.
 * Just the sum of lineItem totals — same as calcEstimateTotals().subtotal.
 */
export function deriveTotal(lineItems) {
  if (!Array.isArray(lineItems) || lineItems.length === 0) return 0;
  return parseFloat(
    lineItems.reduce((s, li) => s + (Number(li.total) || 0), 0).toFixed(2)
  );
}

/**
 * Build a lineItems[] array from the legacy flat-scalar estimate shape.
 * Used when migrating old seed/localStorage data.
 */
export function lineItemsFromScalars({ basePrice = 0, laborCost = 0, materialCost = 0, discount = 0, laborHours = 0, package: pkg = '', material = '', materialColor = '', id = '' }) {
  const items = [];

  const prefix = id ? `${id}-` : '';

  if (basePrice > 0) {
    items.push(makeLineItem({
      id: `${prefix}li-base`,
      description: [pkg, material, materialColor].filter(Boolean).join(' — ') || 'Wrap Service',
      qty: 1,
      unitPrice: basePrice,
      cost: Math.round(basePrice * 0.65),
      total: basePrice,
      category: 'material',
    }));
  }

  if (laborCost > 0) {
    const hrs = Number(laborHours) || 1;
    const rate = parseFloat((laborCost / hrs).toFixed(2));
    items.push(makeLineItem({
      id: `${prefix}li-labor`,
      description: `Labor (${hrs} hr${hrs !== 1 ? 's' : ''})`,
      qty: hrs,
      unitPrice: rate,
      cost: 0,
      total: laborCost,
      category: 'labor',
    }));
  }

  if (materialCost > 0) {
    items.push(makeLineItem({
      id: `${prefix}li-mat`,
      description: material ? `Material Supply — ${material}` : 'Material Supply',
      qty: 1,
      unitPrice: materialCost,
      cost: Math.round(materialCost * 0.55),
      total: materialCost,
      category: 'material',
    }));
  }

  if (discount > 0) {
    items.push(makeLineItem({
      id: `${prefix}li-disc`,
      description: 'Discount',
      qty: 1,
      unitPrice: -discount,
      cost: 0,
      total: -discount,
      category: 'discount',
    }));
  }

  return items;
}

/**
 * Snapshot the current version of an estimate's lineItems.
 * Call before mutating lineItems in updateEstimate().
 */
export function snapshotVersion(estimate) {
  const { subtotal } = calcEstimateTotals(estimate);
  return {
    v: (estimate.currentVersion ?? 0) + 1,
    lineItems: [...(estimate.lineItems ?? [])],
    total: subtotal,
    createdAt: new Date().toISOString(),
    createdBy: estimate.assignedTo ?? null,
    note: '',
  };
}
